import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  ShoppingCart, 
  Users,
  Package, 
  CreditCard,
  QrCode,
  Settings,
  Menu,
  X,
  Clock,
  User,
  LogOut,
  UserCog,
  FileText,
  Table,
  Bot
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { usePermissions, PERMISSIONS } from '../../hooks/usePermissions';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggle }) => {
  const { logout } = useAuth();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();

  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1280;
    }
    return false;
  });

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 1280);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const menuItems = [
    { 
      path: '/dashboard', 
      icon: LayoutDashboard, 
      label: 'Dashboard',
      permission: PERMISSIONS.DASHBOARD_LIMITED 
    },
    { 
      path: '/ai-chatbot', 
      icon: Bot, 
      label: 'AI Assistant',
      permission: PERMISSIONS.DASHBOARD_LIMITED // Allow access to all users for now
    },
    { 
      path: '/orders', 
      icon: ShoppingCart, 
      label: 'Orders',
      permission: PERMISSIONS.ORDERS_VIEW 
    },
    { 
      path: '/order-history', 
      icon: Clock, 
      label: 'Order History',
      permission: PERMISSIONS.HISTORY_LIMITED 
    },
    { 
      path: '/today-report', 
      icon: FileText, 
      label: 'Today\'s Report',
      permission: PERMISSIONS.HISTORY_LIMITED 
    },

    { 
      path: '/customers', 
      icon: Users, 
      label: 'Customers',
      permission: PERMISSIONS.CUSTOMERS_VIEW 
    },
    { 
      path: '/inventory', 
      icon: Package, 
      label: 'Inventory',
      permission: PERMISSIONS.INVENTORY_VIEW 
    },
    { 
      path: '/payments', 
      icon: CreditCard, 
      label: 'Payments',
      permission: PERMISSIONS.PAYMENTS_FULL 
    },
    { 
      path: '/features', 
      icon: QrCode, 
      label: 'Features',
      permission: PERMISSIONS.FEATURES_FULL 
    },
    { 
      path: '/table-view', 
      icon: Table, 
      label: 'Table View',
      permission: PERMISSIONS.ORDERS_VIEW 
    },
    { 
      path: '/user-management', 
      icon: UserCog, 
      label: 'User Management',
      permission: PERMISSIONS.USER_MANAGEMENT 
    },
    { 
      path: '/settings', 
      icon: Settings, 
      label: 'Settings',
      permission: PERMISSIONS.SETTINGS_FULL 
    },
    { 
      path: '/subscription', 
      icon: CreditCard, 
      label: 'Subscription',
      permission: PERMISSIONS.SETTINGS_FULL // Show to owners/admins
    },
  ];

  // Filter menu items based on user permissions
  const allowedMenuItems = menuItems.filter(item => hasPermission(item.permission));

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/signin');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleProfileClick = () => {
    navigate('/profile');
    // Close sidebar on tablet/mobile after navigation
    if (!isDesktop) {
      onToggle();
    }
  };

  const handleNavigation = () => {
    // Close sidebar on tablet/mobile after navigation
    if (!isDesktop) {
      onToggle();
    }
  };

  return (
    <>
      {/* Overlay for tablet/mobile when sidebar is open */}
      {!isCollapsed && !isDesktop && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[9998]"
          onClick={onToggle}
        />
      )}
      
      {/* Sidebar */}
      <div className={`
        ${isDesktop 
          ? 'relative' 
          : 'fixed'
        } left-0 top-0 h-full bg-white dark:bg-gray-800 shadow-lg z-[9999] transition-all duration-300 ease-in-out flex flex-col
        ${isDesktop 
          ? isCollapsed ? 'w-16' : 'w-64'
          : `w-64 ${isCollapsed ? '-translate-x-full' : 'translate-x-0'}`
        }
        ${isDesktop ? 'border-r border-gray-200 dark:border-gray-700' : ''}
      `}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          {(!isCollapsed || !isDesktop) && (
            <h1 className="text-xl desktop:text-2xl font-bold text-primary-600 dark:text-primary-400">
              CafeLawn
            </h1>
          )}
          <button
            onClick={onToggle}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
          >
            {isCollapsed && isDesktop ? (
              <Menu className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            ) : (
              <X className={`w-5 h-5 text-gray-600 dark:text-gray-400 ${isDesktop ? '' : ''}`} />
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className={`flex-1 space-y-1 tablet:space-y-2 overflow-y-auto ${
          isCollapsed && isDesktop ? 'p-2' : 'p-4'
        }`}>
          {allowedMenuItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={handleNavigation}
              >
                {({ isActive }) => (
                  <div className={`group flex items-center px-3 py-2 tablet:py-3 rounded-lg transition-colors duration-200 relative overflow-hidden ${
                    isActive
                      ? isCollapsed && isDesktop
                        ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-white'
                        : 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-white border-r-2 border-primary-600 dark:border-primary-400'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-200'
                  } ${isCollapsed && isDesktop ? 'justify-center px-2' : 'space-x-3'}`}>
                    {/* Animated Accent Bar */}
                    <span className={`absolute left-0 top-0 h-full w-1 bg-primary-600 dark:bg-primary-400 rounded-r transition-all duration-500 ease-out
                      ${isCollapsed && isDesktop ? 'opacity-0' : ''}
                      ${isActive ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0'}
                      group-hover:opacity-100 group-hover:scale-y-100
                    `} style={{transformOrigin: 'top'}} />
                    <Icon className="w-4 h-4 tablet:w-5 tablet:h-5 desktop:w-6 desktop:h-6 flex-shrink-0 transition-transform duration-300 group-hover:rotate-12 group-hover:scale-110" />
                    {(!isCollapsed || !isDesktop) && (
                      <span className="font-medium text-sm tablet:text-base desktop:text-lg">
                        {item.label}
                      </span>
                    )}
                  </div>
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Profile and Logout */}
        <div className={`border-t border-gray-200 dark:border-gray-700 space-y-1 tablet:space-y-2 ${
          isCollapsed && isDesktop ? 'p-2' : 'p-4'
        }`}>
          <button
            onClick={handleProfileClick}
            className={`w-full flex items-center py-2 tablet:py-3 rounded-lg transition-colors duration-200 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-200 ${
              isCollapsed && isDesktop ? 'justify-center px-2' : 'space-x-3 px-3'
            }`}
          >
            <User className="w-4 h-4 tablet:w-5 tablet:h-5 desktop:w-6 desktop:h-6 flex-shrink-0" />
            {(!isCollapsed || !isDesktop) && (
              <span className="font-medium text-sm tablet:text-base desktop:text-lg">
                Profile
              </span>
            )}
          </button>
          
          <button
            onClick={handleLogout}
            className={`w-full flex items-center py-2 tablet:py-3 rounded-lg transition-colors duration-200 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-700 dark:hover:text-red-300 ${
              isCollapsed && isDesktop ? 'justify-center px-2' : 'space-x-3 px-3'
            }`}
          >
            <LogOut className="w-4 h-4 tablet:w-5 tablet:h-5 desktop:w-6 desktop:h-6 flex-shrink-0" />
            {(!isCollapsed || !isDesktop) && (
              <span className="font-medium text-sm tablet:text-base desktop:text-lg">
                Logout
              </span>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar; 